import {
  removeStartPoint,
  selectStartingPointForDayIndex,
} from "@store/plan/planReducer";
import { useAppDispatch, useAppSelector } from "@store/store";
import { FunctionComponent, useCallback, useMemo } from "react";
import { StartingPointLink } from "../StartingPointLink/StartingPointLink";
import { ExternalIcon } from "@icons/ExternalIcon";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { Text } from "@ui-kit/Text/Text";
import { IconButton } from "@ui-kit/IconButton/IconButton";

import "./StartingPoint.css";
import { PlanItemTravelIndicator } from "@modules/entities/PlanItemList/PlanItemTravelIndicator";
import { selectDirections } from "@store/planRouteReducer";

interface StartingPointProps {
  /**
   * Day index of the starting point.
   */
  dayIndex: number | null;
}

/**
 * Display Starting Point indicator if starting point exists for dayIndex or link to add a starting
 * point for that day.
 */
const StartingPoint: FunctionComponent<StartingPointProps> = ({ dayIndex }) => {
  const dispatch = useAppDispatch();
  const directions = useAppSelector(selectDirections);

  const startingPoint = useAppSelector(
    selectStartingPointForDayIndex(dayIndex),
  );

  const handleDeleteStartingPoint = useCallback(() => {
    dispatch(removeStartPoint(startingPoint!));
  }, [dispatch, startingPoint]);

  const direction = useMemo(() => {
    if (!startingPoint) {
      return;
    }

    return directions?.find((x) => {
      const lastItem = x.start.items[x.start.items.length - 1];
      if (lastItem?.id !== startingPoint?.id) {
        return false;
      }

      if (x.end.items[0].dayIndex !== startingPoint?.dayIndex) {
        // Do not show directions when it crosses days.
        return false;
      }

      return true;
    });
  }, [directions, startingPoint]);

  return startingPoint ? (
    <>
      <div className="starting-point-component">
        <div className="location-icon-container">
          <ExternalIcon svgName="location" color={colors.primary60} />
        </div>
        <div className="name">
          <Text variant="labelMedium">{startingPoint.name}</Text>
        </div>
        <div className="delete-button-container">
          <IconButton
            icon={
              <ExternalIcon
                svgName="trash"
                size={16}
                color={colors.iconDanger}
              />
            }
            onPress={handleDeleteStartingPoint}
            type="flat"
          />
        </div>
      </div>
      <PlanItemTravelIndicator
        distanceInMeters={direction?.distanceInMeters}
        timeInSeconds={direction?.timeInSeconds}
      />
    </>
  ) : (
    <StartingPointLink dayIndex={dayIndex} />
  );
};

export { StartingPoint };
